/**
 * Vimeo Facade JS
 * Handles lazy loading and initialization of Vimeo players.
 * Unified SDK Edition with Infinite Scroll & Conflict protection.
 */

class VimeoFacade {
  constructor() {
    this.initialInitDone = false;
    this.sdkPromise = null;
    this.mutationObserver = null;
    this.hasUnlocked = false;

    // Observer for PRE-LOADING (large margin to load early)
    this.preloadObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          const facade = entry.target;
          if (entry.isIntersecting) {
            if (!facade.dataset.initialized) {
              if (facade.dataset.fvBackground === 'true' || facade.dataset.fvAutoplay === 'true') {
                this.loadPlayer(facade);
              }
            }
          }
        });
      },
      {
        threshold: 0,
        rootMargin: '800px'
      }
    );

    // Observer for PLAY/PAUSE (small margin for accurate visibility)
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          const facade = entry.target;
          if (entry.isIntersecting) {
            if (!facade.dataset.initialized) {
              if (facade.dataset.fvBackground === 'true' || facade.dataset.fvAutoplay === 'true') {
                this.play(facade);
              }
            } else if (facade.dataset.fvBackground === 'true') {
              this.play(facade);
            }
          } else {
            if (facade.dataset.initialized) {
              this.pause(facade);
            }
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '50px'
      }
    );

    this.init();
  }

  init() {
    
    
    // Migração inicial (Self-healing): Converte atributos antigos para o novo formato data-fv-
    this.migrateLegacyAttributes(document);

    this.scan(document);
    this.initialInitDone = true;
    
    // Iniciar MutationObserver para lidar com carregamento dinâmico (coleções, filtros, infinite scroll)
    this.initMutationObserver();

    // Pre-load SDK
    this.loadSDK().catch(() => {});
    
    // Global interaction unlock
    this.initInteractionUnlock();
  }

  initInteractionUnlock() {
    let unlockAttempts = 0;
    const maxAttempts = 8;
    const unlock = (e) => {
      unlockAttempts++;

      // Pequeno atraso para o navegador processar o evento de toque
      setTimeout(() => {
        // Tentar play em todas as facades e vídeos nativos visíveis
        const candidates = document.querySelectorAll('[data-fv-facade][data-fv-autoplay="true"], [data-fv-facade][data-fv-background="true"], video[autoplay]');
        candidates.forEach(el => {
          if (this.isElementInViewport(el)) {
            if (el.hasAttribute('data-fv-facade')) {
              if (!el.classList.contains('is-playing')) this.play(el);
            } else if (el.tagName === 'VIDEO' && el.paused) {
              el.play().catch(() => {});
            }
          }
        });
      }, 50);

      this.hasUnlocked = true;

      // Continuar ouvindo até N tentativas (Low Power Mode precisa de mais)
      if (unlockAttempts >= maxAttempts) {
        ['touchstart', 'mousedown', 'scroll'].forEach(ev => {
          document.removeEventListener(ev, unlock);
        });
      }
    };

    ['touchstart', 'mousedown', 'scroll'].forEach(ev => {
      document.addEventListener(ev, unlock, { passive: true });
    });
  }

  migrateLegacyAttributes(root) {
    if (!root || (root.nodeType !== 1 && root.nodeType !== 9)) return;
    
    const legacy = root.querySelectorAll ? root.querySelectorAll('[data-vimeo-facade], [data-vimeo-id]') : [];
    const elementsToMigrate = [...legacy];
    
    // Verificar se o próprio root precisa de migração
    if (root.nodeType === 1 && (root.hasAttribute('data-vimeo-facade') || root.hasAttribute('data-vimeo-id'))) {
      elementsToMigrate.push(root);
    }

    elementsToMigrate.forEach(el => {
      if (el.dataset.vimeoId && !el.dataset.fvId) {
        el.dataset.fvId = el.dataset.vimeoId;
        delete el.dataset.vimeoId;
      }
      if (el.hasAttribute('data-vimeo-facade') && !el.hasAttribute('data-fv-facade')) {
        el.setAttribute('data-fv-facade', '');
        el.removeAttribute('data-vimeo-facade');
      }
      // Migrar outros parâmetros
      ['autoplay', 'loop', 'muted', 'controls', 'background'].forEach(attr => {
        if (el.dataset[attr] && !el.dataset['fv' + attr.charAt(0).toUpperCase() + attr.slice(1)]) {
          const newKey = 'fv' + attr.charAt(0).toUpperCase() + attr.slice(1);
          el.dataset[newKey] = el.dataset[attr];
          delete el.dataset[attr];
        }
      });
    });
  }

  initMutationObserver() {
    if (this.mutationObserver) return;

    this.mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === 1) {
            // Migrar atributos antes de observar
            this.migrateLegacyAttributes(node);
            
            // Se o nó em si for um facade, registrar
            if (node.hasAttribute('data-fv-facade')) {
              this.observeElement(node);
            }
            // Se contiver facades internos, escanear apenas este nó
            else if (node.querySelector('[data-fv-facade]')) {
              this.scan(node);
            }
          }
        });
      });
    });

    this.mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Scans the given root for new facades and starts observing them.
   * Can be called manually by pagination or other scripts.
   * @param {HTMLElement|Document} root 
   */
  scan(root = document) {
    if (!root || (root.nodeType !== 1 && root.nodeType !== 9)) return;
    
    // Migrar atributos no root antes do querySelector
    this.migrateLegacyAttributes(root);

    const facades = root.querySelectorAll('[data-fv-facade]:not([data-observed="true"])');
    if (facades.length > 0) {
      facades.forEach(f => this.observeElement(f));
    }
  }

  observeElement(facade, force = false) {
    if (facade.dataset.observed === 'true' && !force) return;
    this.observer.observe(facade);
    this.preloadObserver.observe(facade);
    facade.dataset.observed = 'true';
    
    // Immediate load if already visible or if it's a priority background video
    if (facade.dataset.fvBackground === 'true' && this.isElementInViewport(facade)) {
      this.loadPlayer(facade);
    }

    // Dynamic thumbnail fetch if needed
    this.fetchThumbnail(facade);
  }

  async fetchThumbnail(facade) {
    const poster = facade.querySelector('.vimeo-facade__poster');
    // If no poster or if it's explicitly requested to use vimeo thumb
    if (poster && poster.hasAttribute('data-fv-use-vimeo-thumb')) {
       const vimeoId = facade.dataset.fvId;
       try {
         const response = await fetch(`https://vimeo.com/api/oembed.json?url=https://vimeo.com/${vimeoId}&width=800`);
         const data = await response.json();
         if (data.thumbnail_url) {
           poster.src = data.thumbnail_url;
           if (poster.srcset) poster.removeAttribute('srcset');
           poster.removeAttribute('data-fv-use-vimeo-thumb');
         }
       } catch (e) {
         console.warn(`📹 VimeoFacade: Failed to fetch thumb for ${vimeoId}`, e);
       }
    }
  }

  isElementInViewport(el) {
    const rect = el.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    const windowWidth = window.innerWidth || document.documentElement.clientWidth;
    
    // Mais permissivo: basta que esteja visível +/- 400px dos limites
    return (
      rect.bottom > -400 &&
      rect.top < windowHeight + 400 &&
      rect.right > -400 &&
      rect.left < windowWidth + 400
    );
  }

  async loadSDK() {
    if (this.sdkPromise) return this.sdkPromise;
    this.sdkPromise = new Promise((resolve, reject) => {
      if (window.Vimeo && window.Vimeo.Player) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = window.VIMEO_PLAYER_URL;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
    return this.sdkPromise;
  }

  async loadPlayer(container) {
    if (container.dataset.initialized === 'true') {
      return container._vimeoPlayer || null;
    }
    if (container.dataset.loading === 'true') {
      return container._loadPromise || container._vimeoPlayer || null;
    }
    
    container.dataset.loading = 'true';
    container._loadPromise = this.loadSDKPlayer(container).then(player => {
      container._loadPromise = null;
      return player;
    });
    return container._loadPromise;
  }

  async loadSDKPlayer(container) {
    await this.loadSDK();
    const vimeoId = container.dataset.fvId;
    const isAutoplay = container.dataset.fvAutoplay === 'true';
    const isBackground = container.dataset.fvBackground === 'true';

    // Limpar qualquer iframe existente injetado por outros scripts ou apps de terceiros
    const existingIframes = container.querySelectorAll('iframe');
    if (existingIframes.length > 0) {
      existingIframes.forEach(f => f.remove());
    }

    const options = {
      id: vimeoId,
      autoplay: isAutoplay || isBackground,
      loop: container.dataset.fvLoop === 'true',
      muted: container.dataset.fvMuted === 'true' || isBackground,
      controls: isBackground ? false : (container.dataset.fvControls === 'true'),
      autopause: false,
      responsive: false,
      quality: container.dataset.fvQuality || 'auto',
      max_quality: container.dataset.fvMaxQuality || (window.innerWidth > 768 ? '720p' : '560p'),
      playsinline: true,
      transparent: true,
      title: false,
      byline: false,
      portrait: false,
      vimeo_logo: false,
      keyboard: false,
      pip: false,
      speed: false,
      texttrack: false,
      dnt: true,
      background: isBackground
    };

    const player = new Vimeo.Player(container, options);
    container._vimeoPlayer = player;
    
    // Initialize playback promise chain if not already there
    if (!container._playbackPromise) {
      container._playbackPromise = Promise.resolve();
    }

    player.ready().then(() => {
      const iframe = container.querySelector('iframe');
      if (iframe) iframe.title = 'Vídeo do produto';
      // NÃO adicionar is-playing aqui — esperar o timeupdate/playing confirmar
      // que frames reais estão sendo renderizados, evitando flash de tela preta
    });

    player.on('play', () => {
      container._pseudoEnded = false;
      // Esperamos o evento 'playing' para mostrar o vídeo
    });
    player.on('timeupdate', (data) => {
      if (data.seconds > 0.1 && !container.classList.contains('is-playing')) {
        container.classList.add('is-playing');
      }
      if (data.percent > 0.99 && !container._pseudoEnded) {
        container._pseudoEnded = true;
        container.dispatchEvent(new CustomEvent('vimeo:ended', { detail: { player, isPseudo: true }, bubbles: true }));
      } else if (data.percent < 0.1) {
        container._pseudoEnded = false;
      }
    });
    player.on('loaded', () => {
      // Metadados carregados, mas ainda não mostramos o vídeo
    });
    player.on('playing', () => {
      // O SDK confirmou que o vídeo está rodando
      container.classList.add('is-playing');
      container.classList.remove('has-error');
    });
    player.on('error', (data) => {
      console.error(`📹 VimeoFacade SDK Error: [${vimeoId}]`, data);
      container.classList.add('has-error');
      container.classList.remove('is-playing');
    });
    player.on('ended', () => {
      container.dispatchEvent(new CustomEvent('vimeo:ended', { detail: { player }, bubbles: true }));
    });

    container.dataset.initialized = 'true';
    return player;
  }

  play(container) {
    const vimeoId = container.dataset.fvId;
    if (!container._playbackPromise) container._playbackPromise = Promise.resolve();

    container._playbackPromise = container._playbackPromise.then(async () => {
      try {
        const player = container._vimeoPlayer || await this.loadPlayer(container);
        if (!player) return;
        
        await player.ready();
        // Pequeno delay tático para garantir que o iframe aceite o comando após o ready
        await new Promise(resolve => setTimeout(resolve, 150));
        
        return player.play().then(() => {
          container.classList.add('is-playing');
          container.classList.remove('has-error');
        }).catch(err => {
          if (err.name === 'NotAllowedError') {
             console.info(`📹 VimeoFacade: [${vimeoId}] Autoplay blocked. Waiting for interaction.`);
             // NÃO remover is-playing aqui — manter o iframe visível
             // para evitar flash de thumbnail. O poster do Vimeo fica por trás.
          } else if (err.name === 'PlayInterrupted') {
            console.info(`📹 VimeoFacade: [${vimeoId}] Play interrupted by pause (Normal).`);
          } else {
            console.error(`📹 VimeoFacade: [${vimeoId}] Play failed.`, err);
            container.classList.add('has-error');
            container.classList.remove('is-playing');
          }
        });
      } catch (err) {
        container.classList.add('has-error');
        container.classList.remove('is-playing');
      }
    });
  }

  pause(container) {
    // NÃO tentar carregar o player só pra pausar — isso causa loading desnecessário
    if (!container._vimeoPlayer) return;
    if (!container._playbackPromise) container._playbackPromise = Promise.resolve();

    container._playbackPromise = container._playbackPromise.then(async () => {
      try {
        const player = container._vimeoPlayer;
        if (!player) return;
        
        await player.ready();
        return player.pause().catch(() => {});
      } catch (err) {}
    });
  }

  setVolume(container, volume) {
    if (!container._playbackPromise) container._playbackPromise = Promise.resolve();

    container._playbackPromise = container._playbackPromise.then(async () => {
      try {
        const player = container._vimeoPlayer || await this.loadPlayer(container);
        if (!player) return;

        await player.ready();
        return player.setVolume(volume).catch(() => {});
      } catch (err) {}
    });
  }

  async togglePlay(container) {
    if (!container._vimeoPlayer) return;
    try {
      const player = container._vimeoPlayer;
      await player.ready();
      const isPaused = await player.getPaused();
      if (isPaused) {
        await player.play();
        container.classList.add('is-playing');
      } else {
        await player.pause();
      }
    } catch (err) {
      console.info('📹 VimeoFacade: togglePlay error', err);
    }
  }

  pauseAll() {
    const activeFacades = document.querySelectorAll('[data-initialized="true"]');
    activeFacades.forEach(facade => this.pause(facade));
  }
}

// Global initialization
if (!window.vimeoFacade) {
  const initVimeo = () => { window.vimeoFacade = new VimeoFacade(); };
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    initVimeo();
  } else {
    document.addEventListener('DOMContentLoaded', initVimeo);
  }
  
  document.addEventListener('shopify:section:load', () => {
    if (window.vimeoFacade) window.vimeoFacade.init();
  });
}
